import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Impersonation state — when a superadmin is viewing a company
  const [impersonating, setImpersonating] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ledgerpro_impersonate') || 'null'); }
    catch { return null; }
  });

  useEffect(() => {
    const token = localStorage.getItem('ledgerpro_token');
    const savedUser = localStorage.getItem('ledgerpro_user');

    // Clear stale impersonation data if no token exists (user logged out)
    if (!token && impersonating) {
      setImpersonating(null);
      localStorage.removeItem('ledgerpro_impersonate');
      localStorage.removeItem('ledgerpro_impersonate_company');
      localStorage.removeItem('ledgerpro_impersonate_subscription');
    }

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // If impersonating, restore company/subscription from localStorage
      // (don't call authAPI.me() — the JWT belongs to the superadmin,
      // so it would overwrite the impersonated user state)
      if (impersonating) {
        try {
          const savedCompany = JSON.parse(localStorage.getItem('ledgerpro_impersonate_company') || 'null');
          const savedSub = JSON.parse(localStorage.getItem('ledgerpro_impersonate_subscription') || 'null');
          if (savedCompany) setCompany(savedCompany);
          if (savedSub) setSubscription(savedSub);
        } catch { /* ignore */ }
        setLoading(false);
      } else {
        // Normal login — refresh user data from the server
        authAPI.me().then(data => {
          setUser(data.user);
          setCompany(data.company);
          setSubscription(data.subscription || null);
          localStorage.setItem('ledgerpro_user', JSON.stringify(data.user));
        }).catch(() => {
          logout();
        }).finally(() => setLoading(false));
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password, companyId) => {
    const data = await authAPI.login({ username, password, companyId });
    // Clear any stale impersonation data from a previous session
    localStorage.removeItem('ledgerpro_impersonate');
    localStorage.removeItem('ledgerpro_impersonate_company');
    localStorage.removeItem('ledgerpro_impersonate_subscription');
    setImpersonating(null);
    localStorage.setItem('ledgerpro_token', data.token);
    localStorage.setItem('ledgerpro_user', JSON.stringify(data.user));
    setUser(data.user);
    setSubscription(data.subscription || null);
    return data;
  };

  // Phone OTP login — same session handling as password login
  const loginWithPhone = async (payload) => {
    const data = await authAPI.phoneLogin(payload);
    localStorage.removeItem('ledgerpro_impersonate');
    localStorage.removeItem('ledgerpro_impersonate_company');
    localStorage.removeItem('ledgerpro_impersonate_subscription');
    setImpersonating(null);
    localStorage.setItem('ledgerpro_token', data.token);
    localStorage.setItem('ledgerpro_user', JSON.stringify(data.user));
    setUser(data.user);
    setSubscription(data.subscription || null);
    return data;
  };

  // Google sign-in — same session handling
  const loginWithGoogle = async (payload) => {
    const data = await authAPI.googleLogin(payload);
    localStorage.removeItem('ledgerpro_impersonate');
    localStorage.removeItem('ledgerpro_impersonate_company');
    localStorage.removeItem('ledgerpro_impersonate_subscription');
    setImpersonating(null);
    localStorage.setItem('ledgerpro_token', data.token);
    localStorage.setItem('ledgerpro_user', JSON.stringify(data.user));
    setUser(data.user);
    setSubscription(data.subscription || null);
    return data;
  };

  // One-click Google signup — creates a blank company + trial, then signs in
  const googleRegister = async (payload) => {
    const data = await authAPI.googleRegister(payload);
    localStorage.removeItem('ledgerpro_impersonate');
    localStorage.removeItem('ledgerpro_impersonate_company');
    localStorage.removeItem('ledgerpro_impersonate_subscription');
    setImpersonating(null);
    localStorage.setItem('ledgerpro_token', data.token);
    localStorage.setItem('ledgerpro_user', JSON.stringify(data.user));
    if (data.user) {
      try { data.user.permissions = JSON.parse(localStorage.getItem('ledgerpro_user'))?.permissions || []; } catch { /* */ }
    }
    setUser(data.user);
    setSubscription(data.subscription || null);
    return data;
  };

  const register = async (data) => {
    const result = await authAPI.register(data);
    localStorage.setItem('ledgerpro_token', result.token);
    localStorage.setItem('ledgerpro_user', JSON.stringify(result.user));
    setUser(result.user);
    setSubscription(result.subscription || null);
    return result;
  };

  const logout = () => {
    localStorage.removeItem('ledgerpro_token');
    localStorage.removeItem('ledgerpro_user');
    localStorage.removeItem('ledgerpro_impersonate');
    localStorage.removeItem('ledgerpro_impersonate_company');
    localStorage.removeItem('ledgerpro_impersonate_subscription');
    setUser(null);
    setCompany(null);
    setImpersonating(null);
  };

  const refreshUser = async () => {
    const data = await authAPI.me();
    setUser(data.user);
    setCompany(data.company);
    setSubscription(data.subscription || null);
    localStorage.setItem('ledgerpro_user', JSON.stringify(data.user));
    return data.user;
  };

  // ============================================================
  // IMPERSONATION — Superadmin views a company's accounting data
  // ============================================================
  const startImpersonation = async (companyId) => {
    const data = await authAPI.impersonate(companyId);
    // Store impersonation info
    const impData = {
      companyId: companyId,
      companyName: data.company?.name,
      companyEmail: data.company?.email,
      // Save the original admin user so we can restore later
      originalUser: user,
    };
    setImpersonating(impData);
    localStorage.setItem('ledgerpro_impersonate', JSON.stringify(impData));

    // Save company & subscription so they survive page refresh
    if (data.company) {
      localStorage.setItem('ledgerpro_impersonate_company', JSON.stringify(data.company));
    }
    if (data.subscription) {
      localStorage.setItem('ledgerpro_impersonate_subscription', JSON.stringify(data.subscription));
    }

    // Temporarily override user context with company admin
    if (data.companyUser) {
      setUser({
        ...data.companyUser,
        // Ensure company_id is set for routing guards
        company_id: data.companyUser.company_id || companyId,
        _originalUserId: user.id,
        _impersonating: true,
      });
    }
    setCompany(data.company);
    setSubscription(data.subscription || null);
    return data;
  };

  const stopImpersonation = () => {
    const originalUser = impersonating?.originalUser;
    setImpersonating(null);
    localStorage.removeItem('ledgerpro_impersonate');
    localStorage.removeItem('ledgerpro_impersonate_company');
    localStorage.removeItem('ledgerpro_impersonate_subscription');
    // Restore original admin user
    if (originalUser) {
      setUser(originalUser);
    }
    // Re-fetch to get fresh data
    authAPI.me().then(data => {
      setUser(data.user);
      setCompany(data.company);
      setSubscription(data.subscription || null);
    }).catch(() => {});
  };

  const hasPermission = (permission) => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Subscription helpers
  const hasActiveSubscription = () => {
    if (!subscription) return false;
    return subscription.status === 'active' || subscription.status === 'trialing';
  };
  const isPendingSubscription = () => subscription?.status === 'pending';
  const isTrialing = () => subscription?.status === 'trialing';
  const trialDaysLeft = () => {
    if (!subscription?.trial_ends_at) return 0;
    const diff = new Date(subscription.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / 86400000));
  };

  // Access control — a company may only use the system with an active plan
  // or during its 14-day trial. Returns 'ok' | 'no_subscription' | 'trial_expired'.
  const getAccessState = () => {
    if (!user) return 'ok'; // not logged in — public routes handle this
    const isPlatformAdmin = !user.company_id && (user.id === 1 || ['Administrator', 'Superadmin'].includes(user.role_name));
    if (isPlatformAdmin || impersonating) return 'ok';
    if (!subscription || !['active', 'trialing', 'pending'].includes(subscription.status)) {
      return 'no_subscription';
    }
    if (subscription.status !== 'active') {
      const trialEnd = subscription.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
      if (!trialEnd || trialEnd < new Date()) return 'trial_expired';
    }
    return 'ok';
  };

  return (
    <AuthContext.Provider value={{
      user, company, subscription, loading,
      login, loginWithPhone, loginWithGoogle, googleRegister, register, logout, refreshUser, hasPermission,
      hasActiveSubscription, isPendingSubscription, isTrialing, trialDaysLeft,
      getAccessState,
      // Impersonation
      impersonating, startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
